// src/components/OrderDetail/OrderDetail.jsx
import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { RiArrowLeftLine, RiCheckLine, RiTimeLine, RiCloseLine } from 'react-icons/ri';
import './OrderDetail.css';
import { BASE_URL } from '../utils/constants';

const OrderDetail = () => {
  const { id } = useParams(); // from URL: /order/:reference
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchOrder = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await fetch(`${BASE_URL}/api/orders/${id}`);
        if (!response.ok) {
          throw new Error(`Failed to fetch order: ${response.status}`);
        }

        const data = await response.json();
        setOrder(data);
      } catch (err) {
        console.error('Order fetch error:', err);
        setError('Unable to load order details. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchOrder();
  }, [id]); // refetch if order ID in URL changes
  //}, [reference]);

  if (loading) {
    return (
      <div className="order-detail-page">
        <div className="loading">Loading order details...</div>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="order-detail-page">
        <div className="error-message">{error || 'Order not found'}</div>
        <Link to="/orders" className="back-btn">
          <RiArrowLeftLine /> Back to Orders
        </Link>
      </div>
    );
  }

  const totalAmount = order.items.reduce((sum, item) => sum + item.totalCost, 0).toFixed(2);

  const getStatusIcon = (status) => {
    if (status === 'successful' || status === 'delivered') return <RiCheckLine className="status-icon success" />;
    if (status === 'failed') return <RiCloseLine className="status-icon failed" />;
    return <RiTimeLine className="status-icon pending" />;
  };

  return (
    <div className="order-detail-page">
      <header className="detail-header">
        <Link to="/orders" className="back-btn">
          <RiArrowLeftLine /> Back to Orders
        </Link>
        <h1>Order #{order.reference}</h1>
      </header>

      <main className="detail-main">
        <div className="detail-grid">
          {/* Customer & Payment Info */}
          <div className="detail-card">
            <h2>Customer & Payment</h2>
            <div className="info-row">
              <span className="label">Name:</span>
              <span>{order.accountName || 'Unknown'}</span>
            </div>
            <div className="info-row">
              <span className="label">Phone:</span>
              <span>{order.phone}</span>
            </div>
            <div className="info-row">
              <span className="label">Payment Status:</span>
              <span className={`status-badge ${order.paymentStatus}`}>
                {getStatusIcon(order.paymentStatus)} {order.paymentStatus.charAt(0).toUpperCase() + order.paymentStatus.slice(1)}
              </span>
            </div>
            <div className="info-row">
              <span className="label">Total Amount:</span>
              <span className="amount">ZMK {totalAmount}</span>
            </div>
            <div className="info-row">
              <span className="label">Created:</span>
              <span>{new Date(order.createdAt).toLocaleString()}</span>
            </div>
          </div>

          {/* Delivery Info */}
          <div className="detail-card">
            <h2>Delivery Details</h2>
            <div className="info-row">
              <span className="label">Status:</span>
              <span className={`status-badge ${order.deliveryStatus}`}>
                {getStatusIcon(order.deliveryStatus)} {order.deliveryStatus.charAt(0).toUpperCase() + order.deliveryStatus.slice(1)}
              </span>
            </div>
            <div className="info-row">
              <span className="label">Estimated Delivery:</span>
              <span>{order.estimatedDeliveryDate ? new Date(order.estimatedDeliveryDate).toLocaleDateString() : 'Not set'}</span>
            </div>
            <div className="info-row">
              <span className="label">Method:</span>
              <span>{order.deliveryMethod || 'Not specified'}</span>
            </div>
            <div className="info-row address">
              <span className="label">Address:</span>
              <div>
                {order.deliveryAddress?.fullName && <div>{order.deliveryAddress.fullName}</div>}
                {order.deliveryAddress?.street && <div>{order.deliveryAddress.street}</div>}
                {order.deliveryAddress?.city && <div>{order.deliveryAddress.city}, {order.deliveryAddress.province}</div>}
                {order.deliveryAddress?.postalCode && <div>Postal: {order.deliveryAddress.postalCode}</div>}
                {order.deliveryAddress?.additionalInfo && <div>Note: {order.deliveryAddress.additionalInfo}</div>}
              </div>
            </div>
          </div>
        </div>

        {/* Items Table */}
        <div className="items-card">
          <h2>Order Items ({order.items.length})</h2>
          <table className="items-table">
            <thead>
              <tr>
                <th>Item</th>
                <th>Price</th>
                <th>Qty</th>
                <th>Size/Color</th>
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              {order.items.map((item, index) => (
                <tr key={index}>
                  <td>{item.name}</td>
                  <td>ZMK {item.price.toFixed(2)}</td>
                  <td>{item.quantity}</td>
                  <td>{item.size || '-'} / {item.color || '-'}</td>
                  <td>ZMK {item.totalCost.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan="4" className="total-label">Grand Total</td>
                <td className="total-amount">ZMK {totalAmount}</td>
              </tr>
            </tfoot>
          </table>
        </div>

        {/* Status History Timeline */}
        <div className="history-card">
          <h2>Status History</h2>
          {order.deliveryStatusHistory?.length > 0 || order.updatedAt ? (
            <div className="timeline">
              {/* Most recent first */}
              {[...(order.deliveryStatusHistory || [])].reverse().map((change, index) => (
                <div key={index} className="timeline-item">
                  <div className="timeline-dot" />
                  <div className="timeline-content">
                    <h3>{change.status.charAt(0).toUpperCase() + change.status.slice(1)}</h3>
                    <p className="timeline-meta">
                      By {change.changedBy || 'System'} • {new Date(change.timestamp).toLocaleString()}
                    </p>
                  </div>
                </div>
              ))}
              {/* Initial creation */}
              <div className="timeline-item">
                <div className="timeline-dot initial" />
                <div className="timeline-content">
                  <h3>Order Created</h3>
                  <p className="timeline-meta">
                    {new Date(order.createdAt).toLocaleString()}
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <p>No status changes recorded yet.</p>
          )}
        </div>
      </main>
    </div>
  );
};

export default OrderDetail;
// import React, { useState } from 'react';
// import {
//   RiArchiveLine,         // from previous fix (replaced RiPackageLine)
//   RiArrowLeftLine,
//   RiEdit2Line,
//   RiDeleteBinLine,       // ← replaced RiTrash2Line (does not exist) with this
//   RiSaveLine
// } from 'react-icons/ri';

// import './OrderDetail.css';

// const OrderDetail = () => {
//   const [note, setNote] = useState('');
//   const [notes, setNotes] = useState([
//     {
//       id: 1,
//       author: 'Admin',
//       date: 'Dec 16, 2024 at 9:00 AM',
//       content: 'Customer requested expedited shipping. Upgraded to express delivery.',
//     },
//     {
//       id: 2,
//       author: 'Warehouse Team',
//       date: 'Dec 16, 2024 at 11:45 AM',
//       content: 'All items picked and packed. Awaiting courier assignment.',
//     },
//   ]);

//   const handleAddNote = () => {
//     if (!note.trim()) return;
//     setNotes([
//       ...notes,
//       {
//         id: Date.now(),
//         author: 'Admin',
//         date: new Date().toLocaleString('en-US', {
//           month: 'short',
//           day: 'numeric',
//           year: 'numeric',
//           hour: 'numeric',
//           minute: '2-digit',
//         }),
//         content: note.trim(),
//       },
//     ]);
//     setNote('');
//   };

//   const orderItems = [
//     { name: 'Wireless Noise-Cancelling Headphones', sku: 'HEAD-ANC-001', qty: 1, price: '$89.99' },
//     { name: 'USB-C Fast Charging Cable (2m)', sku: 'CABLE-USBC-200', qty: 2, price: '$12.52' },
//     { name: 'Portable Power Bank 20000mAh', sku: 'PBANK-20000', qty: 1, price: '$39.99' },
//   ];

//   return (
//     <div className="order-detail-page">
//       <header className="admin-header">
//         <div className="logo-section">
//           <div className="logo-icon">
//             <RiArchiveLine size={24} color="white" />
//           </div>
//           <span className="logo-text">OrderFlow</span>
//         </div>

//         <nav className="admin-nav">
//           <a href="#dashboard">Dashboard</a>
//           <a href="#orders" className="active">Orders</a>
//           <a href="#profile">Profile</a>
//         </nav>
//       </header>

//       <main className="order-detail-main">
//         <div className="page-header">
//           <button className="back-btn">
//             <RiArrowLeftLine size={20} /> Back to Orders
//           </button>
//           <h1>Order #{orderItems.length > 0 ? 'ORD-2024-001842' : 'Loading...'}</h1>
//         </div>

//         <div className="grid-2-cols">
//           {/* Left column - Order & Customer Info */}
//           <div className="info-panel">
//             <div className="card">
//               <h2>Order Summary</h2>
//               <div className="summary-grid">
//                 <div>
//                   <div className="label">Status</div>
//                   <div className="value status-out">Out for Delivery</div>
//                 </div>
//                 <div>
//                   <div className="label">Placed</div>
//                   <div className="value">Dec 15, 2024 at 10:30 AM</div>
//                 </div>
//                 <div>
//                   <div className="label">Customer</div>
//                   <div className="value">Robert Kim</div>
//                 </div>
//                 <div>
//                   <div className="label">Total</div>
//                   <div className="value amount">$142.50</div>
//                 </div>
//                 <div>
//                   <div className="label">Items</div>
//                   <div className="value">4 items</div>
//                 </div>
//                 <div>
//                   <div className="label">Delivery Date</div>
//                   <div className="value">Dec 17, 2024 at 2:45 PM</div>
//                 </div>
//               </div>
//             </div>

//             <div className="card">
//               <h2>Delivery Address</h2>
//               <div className="address-block">
//                 <p><strong>Robert Kim</strong></p>
//                 <p>789 Cedar Lane, Apartment 3C</p>
//                 <p>Northside, Lusaka 10101</p>
//                 <p>Zambia</p>
//                 <div className="special-instructions">
//                   <strong>Special Instructions:</strong><br />
//                   Please leave at security gate if no answer. Call on arrival.
//                 </div>
//               </div>
//             </div>
//           </div>

//           {/* Right column - Items & Notes */}
//           <div className="info-panel">
//             <div className="card">
//               <h2>Order Items</h2>
//               <div className="items-table">
//                 {orderItems.map((item, i) => (
//                   <div key={i} className="item-row">
//                     <div className="item-info">
//                       <div className="item-name">{item.name}</div>
//                       <div className="item-sku">SKU: {item.sku}</div>
//                     </div>
//                     <div className="item-qty">×{item.qty}</div>
//                     <div className="item-price">{item.price}</div>
//                   </div>
//                 ))}
//                 <div className="total-row">
//                   <div className="total-label">Total</div>
//                   <div className="total-value">$142.50</div>
//                 </div>
//               </div>
//             </div>

//             <div className="card notes-card">
//               <h2>Internal Notes</h2>

//               <div className="add-note">
//                 <textarea
//                   value={note}
//                   onChange={e => setNote(e.target.value)}
//                   placeholder="Add internal note..."
//                   rows={3}
//                 />
//                 <button className="save-note-btn" onClick={handleAddNote} disabled={!note.trim()}>
//                   <RiSaveLine size={16} /> Save Note
//                 </button>
//               </div>

//               <div className="notes-list">
//                 {notes.map(n => (
//                   <div key={n.id} className="note-item">
//                     <div className="note-header">
//                       <div className="note-author">
//                         {n.author} • {n.date}
//                       </div>
//                       <div className="note-actions">
//                         <button className="note-btn edit">
//                           <RiEdit2Line size={14} />
//                         </button>
//                         <button className="note-btn delete">
//                           <RiDeleteBinLine size={14} />    {/* ← updated here */}
//                         </button>
//                       </div>
//                     </div>
//                     <p className="note-content">{n.content}</p>
//                   </div>
//                 ))}
//               </div>
//             </div>
//           </div>
//         </div>
//       </main>
//     </div>
//   );
// };

// export default OrderDetail;

// // import React, { useState } from 'react';
// // import { RiPackageLine, RiArrowLeftLine, RiEdit2Line, RiTrash2Line, RiSaveLine } from 'react-icons/ri';
// // import './OrderDetail.css';

// // const OrderDetail = () => {
// //   const [note, setNote] = useState('');
// //   const [notes, setNotes] = useState([
// //     {
// //       id: 1,
// //       author: 'Admin',
// //       date: 'Dec 16, 2024 at 9:00 AM',
// //       content: 'Customer requested expedited shipping. Upgraded to express delivery.',
// //     },
// //     {
// //       id: 2,
// //       author: 'Warehouse Team',
// //       date: 'Dec 16, 2024 at 11:45 AM',
// //       content: 'All items picked and packed. Awaiting courier assignment.',
// //     },
// //   ]);

// //   const handleAddNote = () => {
// //     if (!note.trim()) return;
// //     setNotes([
// //       ...notes,
// //       {
// //         id: Date.now(),
// //         author: 'Admin',
// //         date: new Date().toLocaleString('en-US', {
// //           month: 'short',
// //           day: 'numeric',
// //           year: 'numeric',
// //           hour: 'numeric',
// //           minute: '2-digit',
// //         }),
// //         content: note.trim(),
// //       },
// //     ]);
// //     setNote('');
// //   };

// //   const orderItems = [
// //     { name: 'Wireless Noise-Cancelling Headphones', sku: 'HEAD-ANC-001', qty: 1, price: '$89.99' },
// //     { name: 'USB-C Fast Charging Cable (2m)', sku: 'CABLE-USBC-200', qty: 2, price: '$12.52' },
// //     { name: 'Portable Power Bank 20000mAh', sku: 'PBANK-20000', qty: 1, price: '$39.99' },
// //   ];

// //   return (
// //     <div className="order-detail-page">
// //       <header className="admin-header">
// //         <div className="logo-section">
// //           <div className="logo-icon">
// //             <RiPackageLine size={24} color="white" />
// //           </div>
// //           <span className="logo-text">OrderFlow</span>
// //         </div>

// //         <nav className="admin-nav">
// //           <a href="#dashboard">Dashboard</a>
// //           <a href="#orders" className="active">Orders</a>
// //           <a href="#profile">Profile</a>
// //         </nav>
// //       </header>

// //       <main className="order-detail-main">
// //         <div className="page-header">
// //           <button className="back-btn">
// //             <RiArrowLeftLine size={20} /> Back to Orders
// //           </button>
// //           <h1>Order #{orderItems.length > 0 ? 'ORD-2024-001842' : 'Loading...'}</h1>
// //         </div>

// //         <div className="grid-2-cols">
// //           {/* Left column - Order & Customer Info */}
// //           <div className="info-panel">
// //             <div className="card">
// //               <h2>Order Summary</h2>
// //               <div className="summary-grid">
// //                 <div>
// //                   <div className="label">Status</div>
// //                   <div className="value status-out">Out for Delivery</div>
// //                 </div>
// //                 <div>
// //                   <div className="label">Placed</div>
// //                   <div className="value">Dec 15, 2024 at 10:30 AM</div>
// //                 </div>
// //                 <div>
// //                   <div className="label">Customer</div>
// //                   <div className="value">Robert Kim</div>
// //                 </div>
// //                 <div>
// //                   <div className="label">Total</div>
// //                   <div className="value amount">$142.50</div>
// //                 </div>
// //                 <div>
// //                   <div className="label">Items</div>
// //                   <div className="value">4 items</div>
// //                 </div>
// //                 <div>
// //                   <div className="label">Delivery Date</div>
// //                   <div className="value">Dec 17, 2024 at 2:45 PM</div>
// //                 </div>
// //               </div>
// //             </div>

// //             <div className="card">
// //               <h2>Delivery Address</h2>
// //               <div className="address-block">
// //                 <p><strong>Robert Kim</strong></p>
// //                 <p>789 Cedar Lane, Apartment 3C</p>
// //                 <p>Northside, Lusaka 10101</p>
// //                 <p>Zambia</p>
// //                 <div className="special-instructions">
// //                   <strong>Special Instructions:</strong><br />
// //                   Please leave at security gate if no answer. Call on arrival.
// //                 </div>
// //               </div>
// //             </div>
// //           </div>

// //           {/* Right column - Items & Notes */}
// //           <div className="info-panel">
// //             <div className="card">
// //               <h2>Order Items</h2>
// //               <div className="items-table">
// //                 {orderItems.map((item, i) => (
// //                   <div key={i} className="item-row">
// //                     <div className="item-info">
// //                       <div className="item-name">{item.name}</div>
// //                       <div className="item-sku">SKU: {item.sku}</div>
// //                     </div>
// //                     <div className="item-qty">×{item.qty}</div>
// //                     <div className="item-price">{item.price}</div>
// //                   </div>
// //                 ))}
// //                 <div className="total-row">
// //                   <div className="total-label">Total</div>
// //                   <div className="total-value">$142.50</div>
// //                 </div>
// //               </div>
// //             </div>

// //             <div className="card notes-card">
// //               <h2>Internal Notes</h2>

// //               <div className="add-note">
// //                 <textarea
// //                   value={note}
// //                   onChange={e => setNote(e.target.value)}
// //                   placeholder="Add internal note..."
// //                   rows={3}
// //                 />
// //                 <button className="save-note-btn" onClick={handleAddNote} disabled={!note.trim()}>
// //                   <RiSaveLine size={16} /> Save Note
// //                 </button>
// //               </div>

// //               <div className="notes-list">
// //                 {notes.map(n => (
// //                   <div key={n.id} className="note-item">
// //                     <div className="note-header">
// //                       <div className="note-author">
// //                         {n.author} • {n.date}
// //                       </div>
// //                       <div className="note-actions">
// //                         <button className="note-btn edit">
// //                           <RiEdit2Line size={14} />
// //                         </button>
// //                         <button className="note-btn delete">
// //                           <RiTrash2Line size={14} />
// //                         </button>
// //                       </div>
// //                     </div>
// //                     <p className="note-content">{n.content}</p>
// //                   </div>
// //                 ))}
// //               </div>
// //             </div>
// //           </div>
// //         </div>
// //       </main>
// //     </div>
// //   );
// // };

// // export default OrderDetail;